import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { AppliedCouponType, AppliedGiftCardsType } from "lib/contexts/CheckoutProvider";
import {
  ItemType,
  BillingType,
  ShippingHandlesType,
  ShippingHandleType,
  LoyaltyRedemptionHandle,
  FloWalletRedemptionHandle,
  ThirdPartyAuthType,
  DiscountedProductSelectorDataType,
  DiscountedProductSelectorItemType,
  DiscountedProductSelectorProductType,
  DiscountedProductSelectorProductVariantType,
  DiscountedProductSelectorPayloadType,
  wishlistVisibilityConfigType,
  WishlistConfigType,
  AddOnType,
  AddOnMetaType,
  CombinedProductRemovalItemsType,
  CouponType,
  ExitCouponNudgeConfigType,
  ExitCouponNudgeRuntimeType,
  ExitCouponNudgeVisualConfigType,
} from "lib/types/checkout";
import {
  addHttpIfMissing,
  buildCheckoutRequestPayload,
  filterEmptyValueParameters,
  getUTMParamsFromURL,
  handlePaymentSuccessRedirection,
  isEmptyObj,
  publishPostMessage,
  setCartApiFailed,
  setClarityTag,
} from "lib/utils/helpers";
import { analyticsEvents, analyticsTypes, clartityTags, constants, eventTypes } from "lib/utils/constants";
import { getRequest, postRequest, putRequest } from "lib/core/apiClient";
import { isTruecallerEnabled } from "lib/third-party/truecaller";
import { getIsTwoStepCheckout } from "lib/utils/merchant";
import { getTwoStepAuthToken } from "lib/utils/auth";
import { customBranding } from "lib/utils/customBranding";
import { UserHistoryType, UserLoginType } from "lib/types/user";
import { CheckoutContext } from "lib/contexts/CheckoutProvider";
import { currencyFormatter } from "lib/utils/formatters";
import { t } from "i18next";
import { MerchantContext } from "lib/contexts/MerchantProvider";
import { UserContext } from "lib/contexts/UserProvider";
import { AuthContext } from "lib/contexts/AuthProvider";
import { mutate } from "swr";
import { parseCartResponse, DEFAULT_PROGRESS_BAR } from "@shopflo/ui/progress-bar";
import { MerchantType } from "../types/merchant";
import { RewardsPayloadType } from "lib/types/rewards";
import { getUpsellUrl } from "./upsell";
import { triggerSentryError } from "./error";
import { ErrorType } from "lib/types/error";
import CustomError from "lib/core/customError";
import { AnalyticsProps } from "lib/types/analytics";
import { PopupKey, PopupPayloadMap } from "lib/types/popup";

/**
 * The exit-coupon nudge the user sees is a merge of two sources:
 *  - `visual`: dashboard-authored presentation config, served via the CHECKOUT ui-attributes
 *    (`checkoutMetadata.uiAttributes.exitCouponNudgeConfig`).
 *  - `runtime`: per-checkout eligibility/pricing resolved from the `/exit-coupon` API, held in
 *    CheckoutProvider.
 * Returns `undefined` when there is no runtime data — the coupon isn't applicable (or hasn't been
 * fetched yet), which is also the "don't render the nudge" signal every consumer already gates on.
 */
export const getExitCouponNudgeConfig = (
  visual: ExitCouponNudgeVisualConfigType | undefined,
  runtime: ExitCouponNudgeRuntimeType | undefined,
): ExitCouponNudgeConfigType | undefined => {
  if (!runtime) return undefined;
  return {
    badge: { bgColor: "#2C874A", textColor: "#FFFFFF", htmlText: "" },
    nudge: { bgColor: "#FFFFFF", textColor: "#27272A", htmlText: "" },
    ctaColor: "#18181B",
    additionalSurvey: false,
    ...visual,
    ...runtime,
    couponCode: runtime.couponCode ?? visual?.couponCode,
    couponValue: runtime.couponValue ?? visual?.couponValue ?? "",
  };
};

export const parseItemsFromURL = (data: any) => {
  const items: any = JSON.parse(data ?? "{}");

  if (!Boolean(items?.length)) return;

  for (let i = 0; i < items.length; i++) {
    items[i].item_id = items[i].item_id?.toString();
    items[i].price = (Math.round(items[i].price) / 100)?.toFixed(2);
  }
  return items;
};

/**
 * @param {[any]} data    Items data from API response
 * @param {boolean} inventoryCheck   flag that changes the items quantity based on the available inventory
 *
 * @returns {items[]}  Returns a parsed array of items
 */
export const getItems = (data: any, inventoryCheck: boolean = false) => {
  if (!Boolean(data?.length)) return [];
  let items: ItemType[] = [];
  data.forEach((value: any, itemIndex: number) => {
    let quantity = value.quantity;
    const isAvailable = Boolean(value.status === "AVAILABLE");
    if (!Boolean(isAvailable) && Boolean(inventoryCheck)) {
      quantity = value.inventory_quantity >= 0 ? value.quantity - value.inventory_quantity : value.quantity;
    }
    const item: ItemType = {
      item_id: value.id,
      variant_id: value.item_id,
      price: value.line_price?.total ?? value.price,
      quantity: quantity,
      item_title: value.item_data?.product_name ?? value?.name,
      originalPrice: Boolean(Number(value.item_data?.original_price))
        ? value.item_data?.original_price
        : value.price,
      markupPrice: value.price,
      image: value.item_data?.image ?? value?.image,
      is_freebie: value.is_freebie,
      variant_title: value.item_data?.variant_name,
      is_available: isAvailable ?? false,
      item_properties: isEmptyObj(value.properties) ? value.item_data?.properties : value.properties,
      availableQuantity: value.inventory_quantity ?? 0,
      isMutable: value.item_data?.is_mutable ?? true,
      product_id: value.product_id ?? "",
      item_url: value.item_data?.url ?? "",
      is_discount_alteration: value.is_discount_alteration ?? false,
      is_platform_fee: value.is_platform_fee ?? false,
      hasProductSelector: value.is_selected ? Boolean(value.is_selected) : false,
      appliedItemAddOns: value.item_addons?.filter((item: AddOnType) => !item?.removed) ?? [],
      rawItem: value,
      itemIndex,
      has_components: value.has_components ?? false,
      is_upsell: value?.is_upsell ?? false,
      components: value.item_data?.components ?? [],
    };
    const alreadyPresentIndex = items.findIndex(
      (selectedItem: ItemType) =>
        selectedItem?.price === item?.price && selectedItem?.variant_id === item?.variant_id,
    );
    if (alreadyPresentIndex > -1 && (Boolean(value.is_freebie) || Boolean(value.is_discount_alteration))) {
      items[alreadyPresentIndex].quantity = items[alreadyPresentIndex].quantity + quantity;
      return;
    }
    items.push(item);
  });
  return items;
};

/**
 * @param {any[]} addOnErrorItems   Out-of-stock add-on entries from error_response.metadata.addons
 *
 * @returns {any[]}  Entries shaped like the raw item data from error_response.metadata.items, so they can be merged with them via mergeOutOfStockItems
 */
export const getOutOfStockAddOnItems = (addOnErrorItems: any) => {
  if (!Boolean(addOnErrorItems?.length)) return [];

  return addOnErrorItems.map((addOnErrorItem: any) => {
    return {
      id: addOnErrorItem?.id,
      item_id: addOnErrorItem?.item_id,
      price: addOnErrorItem?.price ?? 0,
      quantity: addOnErrorItem?.quantity ?? 1,
      status: "OUT_OF_STOCK",
      inventory_quantity: 0,
      is_freebie: false,
      is_upsell: false,
      is_discount_alteration: false,
      is_platform_fee: false,
      has_components: false,
      product_id: addOnErrorItem?.variant_id ?? "",
      item_data: {
        product_name: addOnErrorItem?.product_name ?? "",
        image: addOnErrorItem?.image,
        original_price: addOnErrorItem?.price,
        variant_name: addOnErrorItem?.name ?? "",
        is_mutable: false,
        url: "",
        components: [],
      },
    };
  });
};

// Merges the raw OOS items and OOS add-ons from an OUT_OF_STOCK error response into a single raw item list.
export const mergeOutOfStockItems = (items?: any[], addOns?: any[]) => [
  ...(items?.length ? items : []),
  ...getOutOfStockAddOnItems(addOns),
];

export type HandleOutOfStockResponseParams = {
  items: any;
  addons?: any[];
  setOOSItems: (items: ItemType[]) => void;
  openPopup: <K extends PopupKey>(key: K, payload: PopupPayloadMap[K]) => void;
  sendAnalyticsEvent: (props: AnalyticsProps) => void;
};

// Shared handler for an OUT_OF_STOCK checkout response: formats the raw items, updates OOS state, opens the OOS popup, and fires the FLO_ITEM_OOS event.
export const handleOutOfStockResponse = ({
  items,
  addons,
  setOOSItems,
  openPopup,
  sendAnalyticsEvent,
}: HandleOutOfStockResponseParams) => {
  const outOfStockItems = getItems(mergeOutOfStockItems(items, addons), true) ?? [];
  setOOSItems(outOfStockItems);
  openPopup("OOS", {});
  sendAnalyticsEvent({
    eventName: analyticsEvents.FLO_ITEM_OOS,
    eventFor: [analyticsTypes.SF_ANALYTICS],
    eventType: "click",
    metaData: {
      outOfStockItems,
    },
  });
};

export const getBillingDetails = (data: any) => {
  if (!Boolean(data)) {
    return {
      total_payable: 0,
      sub_total: 0,
      total: 0,
      tax: 0,
      rewards: 0,
      discount: 0,
      shipping: 0,
      cod: 0,
      cod_enabled: false,
      taxes_included: true,
      serviceable: true,
      prepaid_total: 0,
      prepaid_discount: 0,
      original_sub_total: 0,
      addon_total: 0,
      gift_card: 0,
    };
  }
  const BillingType: BillingType = {
    total_payable: data.total_payable,
    sub_total: data.sub_total,
    discount: data.discount,
    tax: data.tax,
    rewards: data?.rewards ?? 0,
    shipping: data.shipping ?? -1,
    total: data.total,
    cod: data.cod,
    taxes_included: data.taxes_included,
    cod_enabled: data.cod_enabled,
    serviceable: data.serviceable,
    prepaid_total: data.total,
    prepaid_discount: data.prepaid_discount ?? data.total,
    original_sub_total: data.original_sub_total ?? data.sub_total,
    addon_total: data.addon_total ?? 0,
    gift_card: data.gift_card ?? 0,
    convenience_fee: data.convenience_fee,
  };

  return BillingType;
};

export const getCouponData = (discounts: any) => {
  if (!Boolean(discounts) || !Boolean(discounts?.length)) return [];
  const couponData = discounts
    ?.filter(
      (coupon: any) =>
        !Boolean(coupon?.is_prepaid) &&
        coupon?.coupon_metadata?.coupon_type !== CouponType.EXIT_COUPON &&
        coupon?.coupon_metadata?.coupon_type !== CouponType.PARTNER_OFFER &&
        (coupon?.coupon_metadata?.coupon_type !== CouponType.REWARD_AS_DISCOUNT ||
          (coupon?.coupon_metadata?.coupon_type === CouponType.REWARD_AS_DISCOUNT &&
            (coupon?.coupon_metadata?.coupon_reward_config?.wallet_type === "CRED" ||
              coupon?.coupon_metadata?.coupon_reward_config?.wallet_type === "TICKERTAPE"))),
    )
    .map((coupon: any, index: number) => {
      const discountPercentage = coupon?.coupon_metadata?.concession_amount;
      const data: AppliedCouponType = {
        code: coupon?.discount_code,
        title: coupon?.coupon_metadata?.title ?? "",
        autoApplied: coupon?.auto_applied ?? false,
        isManualCode: false,
        isFreebie: coupon?.is_freebie ?? false,
        discountValue: coupon?.discount,
        isRemovable: coupon?.is_removable ?? false,
        freebieItemCount: coupon?.coupon_metadata?.item_count ?? 0,
        isPrepaid: coupon?.is_prepaid ?? false,
        prepaidValue: coupon?.coupon_metadata?.max_discount ?? 0,
        discountPercentage,
        isReward: coupon?.coupon_metadata?.coupon_type === CouponType.REWARD_AS_DISCOUNT,
        couponType: coupon?.coupon_metadata?.coupon_type,
        couponRewardConfig: {
          amount: coupon?.coupon_metadata?.coupon_reward_config?.amount,
          walletType: coupon?.coupon_metadata?.coupon_reward_config?.wallet_type,
          expiryConfig: coupon?.coupon_metadata?.coupon_reward_config?.expiry_config,
          couponId: coupon?.coupon_metadata?.coupon_reward_config?.coupon_id,
        },
        rewardData: {
          header: coupon?.coupon_metadata?.header,
          description: coupon?.coupon_metadata?.description,
          longDescription: coupon?.coupon_metadata?.long_description,
        },
        isProductSelector:
          Boolean(
            coupon?.show_selector === undefined ||
              coupon?.show_selector === null ||
              coupon?.show_selector === true
              ? true
              : false,
          ) &&
          Boolean(
            Boolean(coupon?.selected_items?.length) ||
              Boolean(
                coupon?.coupon_metadata?.coupon_code === "COD2PREPAID" &&
                  coupon?.cart_alterations?.some(
                    (alteration: any) => alteration?.type === "SHOW_PRODUCT_SELECTOR",
                  ),
              ),
          ),
      };
      return data;
    });
  return couponData;
};

export const getGiftCardData = (rewards: AppliedGiftCardsType[]) => {
  if (!Boolean(rewards) || !Boolean(rewards?.length)) return [];
  const giftCardData = rewards
    ?.filter((reward: any) => reward.type === "GC")
    .map((giftCard: any) => {
      const data: AppliedGiftCardsType = {
        cardId: giftCard?.src_reference_id,
        cardValue: giftCard?.amount,
        displayName: giftCard?.metadata?.code,
      };
      return data;
    });
  return giftCardData;
};

export const getAppliedLoyaltyData = (rewards: any) => {
  if (!Boolean(rewards) || !Boolean(rewards?.length)) {
    const data: LoyaltyRedemptionHandle = {
      id: "",
      title: "",
      desc: "",
      totalPoints: 0,
      totalPrice: 0,
      provider: "NONE",
    };
    return data;
  }
  const loyaltyData = rewards?.filter((reward: any) => reward.type === "LOYALTY")[0];
  const data: LoyaltyRedemptionHandle = {
    id: loyaltyData?.metadata?.redemption_id,
    title: "",
    desc: "",
    totalPoints: loyaltyData?.metadata?.points,
    totalPrice: loyaltyData?.amount,
    provider: loyaltyData?.provider,
  };
  return data;
};

export const getAppliedFloWalletData = (rewards: any) => {
  if (!Boolean(rewards) || !Boolean(rewards?.length))
    return {
      earnMultiplier: 1,
      totalReducedPoints: 0,
      totalReductionAmount: 0,
    };
  const walletData = rewards?.filter((reward: any) => reward.type === RewardsPayloadType.REWARDS_WALLET)?.[0];
  const data: FloWalletRedemptionHandle = {
    earnMultiplier: 1,
    totalReducedPoints: walletData?.metadata?.points ?? 0,
    totalReductionAmount: walletData?.amount ?? 0,
  };
  return data;
};

/**
 * @param {AppliedCouponType[]} appliedCoupons   Applied coupons list
 *
 * @returns   true if there is at least one autoapplied coupon otherwise false.
 */

export const hasAutoAppliedCoupon = (appliedCoupons: AppliedCouponType[]) => {
  return Boolean(appliedCoupons?.find((coupon) => coupon?.autoApplied === true));
};

/**
 * @param {[any]} allCoupons   All coupons list
 *
 * @returns {string}  Returns the Coupond ID of the matched coupon code
 */
export const findIdCouponByCode = (coupons: any[], code: string) => {
  if (!Boolean(coupons?.length) || !Boolean(code)) return;

  const matchedCoupon = coupons.find((coupon: any) => coupon.coupon_details?.coupon_code === code);
  if (!Boolean(matchedCoupon)) return;
  return matchedCoupon?.coupon_details?.coupon_id;
};

export const getDefaultShippingHandle = (handles: ShippingHandlesType) => {
  if (!Boolean(handles) || !Boolean(handles.length)) return;

  let defaultHandle: ShippingHandleType = { ...handles[0] };
  handles.forEach((handle: ShippingHandleType) => {
    if (Boolean(handle.selected_handle)) {
      defaultHandle = { ...handle };
    }
  });
  return defaultHandle;
};

export const getCODShippingHandles = (handles: ShippingHandlesType) => {
  if (!Boolean(handles) || !Boolean(handles.length)) return [];
  return handles.filter((handle) => handle?.cod_enabled);
};

/**
 * @param {[any]} allCoupons    All coupons list
 * @param {string} couponCode   Coupon code whose stackability should be checked
 *
 * @returns {boolean}  Returns if the given coupon code is stackable or not
 */
export const isCouponStackable = (coupons: any, couponCode: string) => {
  if (!Boolean(coupons?.length) || !Boolean(couponCode)) return false;

  const couponData = coupons?.find((coupon: any) => coupon?.coupon_details?.coupon_code === couponCode);
  return Boolean(couponData?.un_applicability_reason?.reason === "UARC_002");
};

/**
 * @param {[any]} allCoupons    All coupons list
 *
 * @returns {number}  Returns the maximum discount amount possible. This only considers Shopflo discounts.
 * @returns {string}  Returns the maximum discount code possible. This only considers Shopflo discounts.
 */
export const getMaxCouponDiscount = (coupons: any) => {
  let maxCouponDiscountAmount: number = 0;
  let maxCouponDiscountCode: string = "";
  if (!Boolean(coupons?.length)) return { maxCouponDiscountAmount, maxCouponDiscountCode };
  coupons.forEach((coupon: any) => {
    if (
      (coupon.applicable || coupon?.un_applicability_reason?.reason === "UARC_002") &&
      coupon.coupon_details?.tags?.includes("SHOPFLO")
    ) {
      if (maxCouponDiscountAmount < coupon.total_discount) {
        maxCouponDiscountAmount = coupon.total_discount;
        maxCouponDiscountCode = coupon.coupon_details?.coupon_code;
      }
    }
  });
  return { maxCouponDiscountAmount, maxCouponDiscountCode };
};

export const getTotalItems = (cartItems: any) => {
  if (!Boolean(cartItems)) return 0;
  let count = 0;
  cartItems?.forEach((cartItem: any) => (count += cartItem.quantity));
  return count;
};

/**
 * @param {any} itemProperties    List of all the item level properties from Shopify Cart.js API
 * @param {string[]} merchantItemProperties    Properties that a merchant would like to show under each line item in checkout
 *
 * @returns {string}  Returns String of item level properties
 */
export const getItemPropertiesValues = (
  itemProperties: any,
  merchantItemProperties: string[],
  showAllItemProperties: boolean,
) => {
  if (isEmptyObj(itemProperties)) return "";
  let parsedItemProperties: string = "";
  const filteredItemProperties = getFileredItemProperties(
    itemProperties,
    merchantItemProperties,
    showAllItemProperties,
  );

  for (let key in filteredItemProperties) {
    if (filteredItemProperties.hasOwnProperty(key)) {
      parsedItemProperties += `${filteredItemProperties[key]} / `;
    }
  }

  parsedItemProperties = parsedItemProperties.trim();
  if (parsedItemProperties.charAt(parsedItemProperties.length - 1) === "/") {
    parsedItemProperties = parsedItemProperties.slice(0, -2);
  }
  return parsedItemProperties;
};

export const getFileredItemProperties = (
  inputItemProperties: any,
  merchantItemProperties: string[],
  showAllItemProperties: boolean,
  isItemPropertyTransformerConfigured?: boolean,
) => {
  if (
    isEmptyObj(inputItemProperties) ||
    (!Boolean(showAllItemProperties) && !Boolean(merchantItemProperties.length))
  )
    return {};

  const itemProperties = Object.fromEntries(
    Object.entries(inputItemProperties).filter(([key]) => !key.startsWith("_")),
  );

  if (showAllItemProperties) {
    if (isItemPropertyTransformerConfigured) {
      return filterEmptyValueParameters(itemProperties);
    }
    return itemProperties;
  }

  const filteredItemProperties: any = {};
  merchantItemProperties?.forEach((property: string) => {
    const matchedProperty = itemProperties[property];
    if (Boolean(matchedProperty)) {
      filteredItemProperties[property] = matchedProperty;
    }
  });

  return isItemPropertyTransformerConfigured
    ? filterEmptyValueParameters(filteredItemProperties)
    : filteredItemProperties;
};

/**
 * Runs the same per-item filtering as getFileredItemProperties over a list of items,
 * stopping at the first item whose filtered properties are non-empty. Used to decide
 * whether "view details" affordances should render at all, without filtering every item.
 */
export const hasAnyItemProperties = (
  items: ItemType[],
  merchantItemProperties: string[],
  showAllItemProperties: boolean,
): boolean => {
  if (!Boolean(showAllItemProperties) && !Boolean(merchantItemProperties.length)) return false;
  return (
    items?.some((item) => {
      const itemProperties = item?.item_properties;
      if (isEmptyObj(itemProperties)) return false;

      const isItemPropertyTransformerConfigured = Object.values(itemProperties).some(
        (value) => typeof value === "object" && value !== null,
      );

      const filteredItemProperties = getFileredItemProperties(
        itemProperties,
        merchantItemProperties,
        showAllItemProperties,
        isItemPropertyTransformerConfigured,
      );
      return !isEmptyObj(filteredItemProperties);
    }) ?? false
  );
};

export const parseThirdpartyAuthReq = (data: any) => {
  const isThirdPartyAuthEnabled = data?.external_auth_enabled;
  if (!isThirdPartyAuthEnabled) return [];
  const parsedData: ThirdPartyAuthType = {
    provider: data?.auth_provider,
    redirectUrl: data?.metadata?.deep_link_url,
    requestId: data?.request_id,
  };
  return [parsedData];
};

/**
 * Publishes a cart update event to the parent window using postMessage.
 *
 * @param {Array} cartItems - An array of cart items to publish.
 * @param {string} cartItems.item_id - The unique identifier for the cart item.
 * @param {number} cartItems.quantity - The quantity of the cart item.
 * @returns {void}
 */
export const publishCartUpdate = (cartItems: any) => {
  try {
    if (!cartItems || !Array.isArray(cartItems)) return;
    const publishPayload: any = [];
    cartItems.forEach((item: any) => {
      const itemPayload = {
        id: item.variant_id,
        quantity: item.quantity,
        productId: item.product_id,
        price: item.price,
        isFreebie: item.is_freebie ?? false,
      };
      publishPayload.push(itemPayload);
    });
    publishPostMessage(eventTypes.CART_UPDATE, { items: publishPayload });
  } catch (e) {
    console.error(e);
  }
};

export const publishItemUpdate = (
  itemId: any,
  cartItems: any,
  type: "increment" | "decrement" | "delete" | "set",
  isCartItem: boolean = false,
) => {
  try {
    if (itemId < 0 || !cartItems) return;
    const itemData = cartItems.find((item: any) => parseInt(item.item_id) === parseInt(itemId));
    if (!itemData) return;
    const publishPayload: any = {
      id: itemData.variant_id,
      quantity: type === "delete" ? 0 : itemData.quantity,
      productId: itemData.product_id,
      price: itemData.price,
      isFreebie: itemData.is_freebie ?? false,
      shopfloId: itemData.item_properties?.shopflo_id ?? null,
    };
    publishPostMessage(eventTypes.ITEM_UPDATE, { item: publishPayload, isCartItem });
  } catch (e) {
    console.error(e);
  }
};

export const getProgressBarConfig = (values: any) => {
  if (!values || isEmptyObj(values)) return DEFAULT_PROGRESS_BAR;
  const { amountBased, countBased } = parseCartResponse(values);
  const activeProgressBar = amountBased.isEnabled ? amountBased : countBased;
  return activeProgressBar;
};

export const handleSuccessRedirection = ({
  orderStatusUrl,
  originUrl,
  tokenId,
  checkoutId,
}: {
  orderStatusUrl?: string;
  originUrl?: string;
  tokenId?: string;
  checkoutId?: string | null;
}) => {
  const redirectUrl = Boolean(orderStatusUrl)
    ? orderStatusUrl
    : addHttpIfMissing(`${originUrl}/pages/order-status?tokenId=${tokenId}&checkoutId=${checkoutId}`);
  if (redirectUrl) handlePaymentSuccessRedirection(redirectUrl);
};

export const redirectOnSuccessfulOrder = async ({ tokenId }: { tokenId: string | null }) => {
  if (tokenId) {
    const response = await postRequest(`/v2/checkout-tokens/${tokenId}/checkout`, {}, "CHECKOUT_PUBLIC");
    const { token } = response;
    if (Boolean(token?.order?.id)) {
      handleSuccessRedirection({
        originUrl: token?.data?.origin_url,
        orderStatusUrl: token?.order?.order_status_url,
        tokenId: token?.uid,
      });
      return;
    }
  }
};

const handleAuthenticatedCheckout = async ({
  checkoutId,
  checkoutIdParam,
  tokenId,
  utmParams,
  oldCheckoutIdParam,
  checkoutType,
}: {
  checkoutId: string;
  checkoutIdParam: string | null;
  tokenId: string;
  utmParams: any[];
  oldCheckoutIdParam?: string | null;
  checkoutType?: string | null;
}) => {
  const existingId = checkoutId || checkoutIdParam;
  const payload = buildCheckoutRequestPayload(utmParams, oldCheckoutIdParam, checkoutType);

  if (existingId) {
    // CheckoutId is present means user is logged in we need to set user context
    const response = await putRequest(
      `/checkout/${checkoutId || checkoutIdParam}/user`,
      {
        ...payload,
      },
      "KRATOS_PRIVATE",
    );
    return { checkout: response };
  } else {
    // If not just fetch details user already logged in and context is present
    return await postRequest(`/token/${tokenId}/checkout`, payload, "KRATOS_PRIVATE");
  }
};

const handleAuthenticatedCartCheckout = async ({
  checkoutId,
  checkoutIdParam,
  utmParams,
}: {
  checkoutId: string;
  checkoutIdParam: string | null;
  utmParams: any[];
}) => {
  const existingId = checkoutId || checkoutIdParam;

  if (existingId) {
    return await postRequest(
      `/cart/${existingId}/checkout`,
      {
        ...(utmParams?.length && { note_attributes: utmParams }),
      },
      "KRATOS_PRIVATE",
    );
  }
};

// Helper function to handle unauthenticated checkout
const handleUnauthenticatedCheckout = async ({
  checkoutId,
  checkoutIdParam,
  tokenId,
  utmParams,
  checkoutType,
}: {
  checkoutId: string;
  checkoutIdParam: string | null;
  tokenId: string;
  utmParams: any[];
  checkoutType?: string | null;
}) => {
  const existingId = checkoutId || checkoutIdParam;

  const promises = [
    existingId
      ? postRequest(`/cart/${existingId}/checkout`, {}, "KRATOS_PUBLIC")
      : postRequest(
          `/token/${tokenId}/checkout`,
          buildCheckoutRequestPayload(utmParams, undefined, checkoutType),
          "KRATOS_PUBLIC",
        ),
  ];

  if (isTruecallerEnabled()) {
    promises.push(postRequest(`/auth-callback/initiate`, { token_id: tokenId }, "AUTH"));
  }

  const responses = await Promise.allSettled(promises);
  if (responses[0]?.status === "rejected") {
    throw responses[0]?.reason;
  }

  return {
    checkoutTokenResponse: responses[0].value,
    authResponse: responses[1]?.status === "fulfilled" ? responses[1].value : null,
  };
};

export const initializeCheckout = async ({
  tokenId,
  checkoutIdParam,
  oldCheckoutIdParam,
  hashParam,
  checkoutType,
  authContext,
  userContext,
  checkoutContext,
  merchantContext,
  analytics,
  isCheckoutInteractive,
  referrer,
}: {
  tokenId: string | null;
  checkoutIdParam: string | null;
  oldCheckoutIdParam?: string | null;
  hashParam?: string;
  checkoutType?: string | null;
  authContext: AuthContext;
  userContext: UserContext;
  checkoutContext: CheckoutContext;
  merchantContext: MerchantContext;
  analytics: ReturnType<typeof useSendAnalyticsEvent>;
  isCheckoutInteractive?: boolean;
  referrer?: string;
}) => {
  if (!Boolean(tokenId)) return;
  const {
    actions: { setUserData },
  } = userContext;
  const {
    state: { isAuthenticated },
    actions: { setThirdPartyAuth, setLockDiscounts },
  } = authContext;
  const {
    actions: { checkoutInit, setCheckoutLoading },
    state: { checkoutId },
  } = checkoutContext;
  const {
    actions: { setAddressConfig },
  } = merchantContext;
  const { setThirdPartyAnalyticsData, setCheckoutChannel, setClientMetadata, setLongSessionId } = analytics;

  const utmParams = getUTMParamsFromURL();
  try {
    setCheckoutLoading(true);
    let checkoutTokenResponse: any;

    if (isAuthenticated && referrer !== "cart") {
      checkoutTokenResponse = await handleAuthenticatedCheckout({
        checkoutId,
        checkoutIdParam,
        tokenId: tokenId ?? "",
        utmParams,
        oldCheckoutIdParam,
        checkoutType,
      });
    } else if (isAuthenticated && referrer === "cart") {
      checkoutTokenResponse = await handleAuthenticatedCartCheckout({
        checkoutId,
        checkoutIdParam,
        utmParams,
      });
    } else {
      const response = await handleUnauthenticatedCheckout({
        checkoutId,
        checkoutIdParam,
        tokenId: tokenId!,
        utmParams,
        checkoutType,
      });

      checkoutTokenResponse = response.checkoutTokenResponse;
      const authResponse = response.authResponse;

      // Handle special checkout scenarios
      const isTwoStepCheckout =
        getIsTwoStepCheckout(checkoutTokenResponse?.merchant_attributes) &&
        Boolean(getTwoStepAuthToken());
      const isCheckoutC2P = Boolean(checkoutTokenResponse?.checkout?.metadata?.c2p?.enabled);

      if (
        (Boolean(hashParam?.length) && (hashParam === "#COD" || hashParam === "#PAYMENTS")) ||
        (isTwoStepCheckout && isCheckoutC2P)
      ) {
        checkoutTokenResponse = await postRequest(`/checkout/v2/checkout-tokens/${tokenId}/checkout`, {
          ...(checkoutType && { checkout_type: checkoutType }),
        });
      }

      // Handle third-party auth response
      if (authResponse) {
        const parsedAuthData = parseThirdpartyAuthReq(authResponse);
        setThirdPartyAuth(parsedAuthData);
      }

      // Handle prefill phone
      const prefillPhone = checkoutTokenResponse?.checkout?.metadata?.user_details?.phone;
      if (prefillPhone) {
        setUserData({ phone: prefillPhone });
      }
    }

    if (!checkoutTokenResponse) return;
    checkoutInit(checkoutTokenResponse);

    // checkout object dependiencies
    const checkoutResponse = checkoutTokenResponse?.checkout;
    const analyticsMeta = checkoutResponse?.metadata?.analytics;
    setClarityTag(clartityTags.MERCHANT_ID, checkoutResponse?.merchant_id);
    setClarityTag(clartityTags.CHANNEL, checkoutResponse?.metadata?.application_id);
    setThirdPartyAnalyticsData(analyticsMeta);
    setCheckoutChannel(checkoutResponse?.metadata?.application_id);
    setClientMetadata(checkoutResponse?.metadata?.client_meta);
    setLongSessionId(checkoutResponse?.metadata?.long_session_id);

    // merchant response dependencies
    const merchantAttributesResponse = checkoutTokenResponse?.merchant_attributes;
    if (merchantAttributesResponse) {
      const attributes = merchantAttributesResponse?.attributes;
      window.analyticsEndpoint = attributes?.spark_endpoint;
      setClarityTag(clartityTags.SHOP_NAME, attributes?.shop_name);
    }

    // merchant address config dependencies
    const allowedAddressFields: any = checkoutTokenResponse?.address_config?.fields;
    if (allowedAddressFields) {
      setAddressConfig(allowedAddressFields);
    }

    // tokendata dependencies
    const tokenData: any = checkoutTokenResponse?.token;

    return {
      checkoutResponse,
      tokenData,
      isCheckoutInteractive,
    };
  } catch (error: any) {
    if (error instanceof CustomError) {
      triggerSentryError(error, ErrorType.CHECKOUT_ERROR);
    }
    throw error;
  } finally {
    setLockDiscounts(false);
    setCheckoutLoading(false);
  }
};

export const initializeCart = async ({
  authContext,
  userContext,
  checkoutContext,
  merchantContext,
  analytics,
  isCheckoutInteractive,
  isUpdate,
  cartPayload,
  tokenId,
}: {
  authContext: AuthContext;
  userContext: UserContext;
  checkoutContext: CheckoutContext;
  merchantContext: MerchantContext;
  analytics: ReturnType<typeof useSendAnalyticsEvent>;
  isCheckoutInteractive?: boolean;
  isUpdate?: boolean;
  cartPayload: Record<string, any>;
  tokenId?: string;
}) => {
  const {
    actions: { setUserData },
  } = userContext;
  const {
    state: { isAuthenticated },
    actions: { setLockDiscounts },
  } = authContext;
  const {
    actions: { checkoutInit, setCoupons },
  } = checkoutContext;
  const {
    actions: { setAddressConfig },
  } = merchantContext;

  // Records the cart api result in session storage and notifies the parent so the storefront can
  // react to (or recover from) a broken cart. On failure the error is still surfaced to the caller.
  const publishCartApiResult = (hasFailed: boolean) => {
    setCartApiFailed(hasFailed);
    publishPostMessage(hasFailed ? eventTypes.CART_API_FAILED : eventTypes.CART_API_SUCCESS, {
      cartApiFailed: hasFailed,
    });
  };
  const { setThirdPartyAnalyticsData, setCheckoutChannel, setClientMetadata, setLongSessionId } = analytics;

  try {
    let checkoutTokenResponse: any;

    // Backward compatibility to also support tokenId flow
    let payload: Record<string, any> = {
      sf_token_id: tokenId,
    };
    if (cartPayload) {
      payload = cartPayload;
    }

    if (isAuthenticated) {
      try {
        checkoutTokenResponse = await postRequest(`/cart`, payload, "KRATOS_PRIVATE");
        publishCartApiResult(false);
      } catch (error) {
        publishCartApiResult(true);
        throw error;
      }
      const checkoutId = checkoutTokenResponse?.checkout?.uid;
      if (isUpdate) {
        try {
          const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
          const discountResponse = await getRequest(DISCOUNT_PATH, "KRATOS_PRIVATE");
          setCoupons(discountResponse);
        } catch (error) {
          // Silently handle discount API failure - don't let it break the main flow
          console.warn("Failed to fetch discount data:", error);
        }
      }
    } else {
      try {
        checkoutTokenResponse = await postRequest(`/cart`, payload, "KRATOS_PUBLIC");
        publishCartApiResult(false);
      } catch (error) {
        publishCartApiResult(true);
        throw error;
      }
      const checkoutId = checkoutTokenResponse?.checkout?.uid;
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      if (isUpdate) {
        try {
          const discountResponse = await getRequest(DISCOUNT_PATH, "KRATOS_PUBLIC");
          setCoupons(discountResponse);
        } catch (error) {
          // Silently handle discount API failure - don't let it break the main flow
          console.warn("Failed to fetch discount data:", error);
        }
      }

      const prefillPhone = checkoutTokenResponse?.checkout?.metadata?.user_details?.phone;
      if (prefillPhone) {
        setUserData({ phone: prefillPhone });
      }
    }

    if (!checkoutTokenResponse) return;
    checkoutInit(checkoutTokenResponse);

    const checkoutResponse = checkoutTokenResponse?.checkout;
    setClarityTag(clartityTags.MERCHANT_ID, checkoutResponse?.merchant_id);
    setClarityTag(clartityTags.CHANNEL, checkoutResponse?.metadata?.application_id);

    const merchantAttributesResponse = checkoutTokenResponse?.merchant_attributes;

    const attributes = merchantAttributesResponse?.attributes;
    window.analyticsEndpoint = attributes?.spark_endpoint;
    setClarityTag(clartityTags.SHOP_NAME, attributes?.shop_name);

    const allowedAddressFields: any = checkoutTokenResponse?.address_config?.fields;
    setAddressConfig(allowedAddressFields);

    const tokenData: any = checkoutTokenResponse?.token;

    const analyticsMeta = checkoutResponse?.metadata?.analytics;
    setThirdPartyAnalyticsData(analyticsMeta);
    setCheckoutChannel(checkoutResponse?.metadata?.application_id);
    setClientMetadata(checkoutResponse?.metadata?.client_meta);
    setLongSessionId(checkoutResponse?.metadata?.long_session_id);
    return {
      checkoutResponse,
      tokenData,
      isCheckoutInteractive,
    };
  } catch (error: any) {
    if (error instanceof CustomError) {
      triggerSentryError(error, ErrorType.CART_ERROR);
    }
    throw error;
  } finally {
    setLockDiscounts(false);
  }
};

export const getItemsTotalAndValue = (items: any, total: number, showOrderSummaryTotal: boolean) => {
  const totalItems = getTotalItems(items);
  let itemsTotalAndValueStr = "";
  itemsTotalAndValueStr = t("total_items", { count: totalItems });
  if (Boolean(showOrderSummaryTotal)) {
    itemsTotalAndValueStr = `${itemsTotalAndValueStr} • ${currencyFormatter(total)}`;
  }
  return itemsTotalAndValueStr;
};

export const isOnlyFreebieAutoApplied = (appliedCoupons: AppliedCouponType[]) => {
  return Boolean(
    appliedCoupons.every(
      (coupon: AppliedCouponType) =>
        coupon?.isFreebie &&
        coupon?.autoApplied &&
        !coupon?.isRemovable &&
        coupon?.discountPercentage === 100,
    ),
  );
};

export const getRedirectUrl = (redirectUrlResponse: string, merchantId: string) => {
  if (Boolean(redirectUrlResponse)) return redirectUrlResponse;
  const TRAYA_MID = "fa2fd878-be4e-4466-aad4-7906b6543422";
  if (merchantId === TRAYA_MID) return `null`;
  return "";
};

export const showShippingChangeToast = (prevValue: number, currValue: number): boolean => {
  return (
    !(prevValue === -1 && currValue === 0) &&
    !(prevValue === 0 && currValue === -1) &&
    prevValue !== currValue
  );
};

export const getCheckoutExpiredParams = (errorMessage: string) => {
  const isInvalidUser = errorMessage === "C2P flow inaccessible due to user change";
  return { expired: true, isInvalidUser };
};

export const getDiscountedProductSelectorData = (discounts: any): DiscountedProductSelectorDataType => {
  const DiscountedProductSelectorItems: DiscountedProductSelectorItemType[] = [];
  let totalQuantity = 0;
  let totalSelectedQuantity = 0;

  const selectedCoupons = discounts?.filter(
    (coupon: any) =>
      coupon?.coupon_metadata?.coupon_type === CouponType.BXGY &&
      Boolean(
        coupon?.show_selector === undefined ||
          coupon?.show_selector === null ||
          coupon?.show_selector === true
          ? true
          : false,
      ),
  );

  selectedCoupons?.forEach((coupon: any) => {
    const concessionAmount = coupon?.coupon_metadata?.concession_amount;
    const selectedItems: any[] = coupon?.selected_items;
    coupon?.cart_alterations?.forEach((alteration: any) => {
      let selectedQuantity = 0;
      if (alteration?.type !== "SHOW_PRODUCT_SELECTOR") return;
      const products: DiscountedProductSelectorProductType[] = [];
      alteration?.product_details?.forEach((product: any) => {
        const primaryImage = product?.images?.find((image: any) => image?.is_primary)?.src;
        const productImage = Boolean(primaryImage)
          ? primaryImage
          : Boolean(product?.images?.[0]?.src)
          ? product?.images?.[0]?.src
          : "";
        const productVariants: DiscountedProductSelectorProductVariantType[] = [];
        product?.variants?.forEach((variant: any) => {
          const variantImage = product?.images?.find((image: any) =>
            image?.linked_variant_ids?.includes(variant?.variant_id),
          )?.src;
          const selectedVariant = selectedItems?.find(
            (selectedQuantity: any) => selectedQuantity?.variant_id === variant?.variant_id,
          );
          let selectedVariantQuantity = 0;
          if (Boolean(selectedVariant)) {
            selectedVariantQuantity = selectedVariant?.quantity;
            selectedQuantity += selectedVariant?.quantity;
            totalSelectedQuantity += selectedVariant?.quantity;
          }

          productVariants.push({
            id: variant?.variant_id,
            name: variant?.name === "Default Title" ? "" : variant?.name,
            inventoryPolicy: variant?.inventory_policy ?? "DENY",
            selectedQuantity: selectedVariantQuantity,
            isOutOfStock: variant?.out_of_stock,
            inventoryQuantity: variant?.inventory_quantity,
            originalPrice: variant?.original_price,
            currentPrice: variant?.current_price,
            discountedPrice: variant?.discounted_price,
            imageUrl: Boolean(variantImage) ? variantImage : productImage,
            hasComponents: product?.has_variants_that_requires_components,
          });
        });
        const modifiedProductVariants = productVariants
          .sort((a, b) => b.selectedQuantity - a.selectedQuantity)
          .filter((variant) => !variant.isOutOfStock);
        products.push({
          id: product?.product_id,
          name: product?.name,
          primaryImageUrl: productImage,
          variants: modifiedProductVariants,
        });
      });
      const modifiedProducts = products.sort(
        (a, b) =>
          b.variants.filter((variant) => Boolean(variant.selectedQuantity)).length -
          a.variants.filter((variant) => Boolean(variant.selectedQuantity)).length,
      );
      DiscountedProductSelectorItems.push({
        couponCode: coupon?.discount_code,
        quantity: alteration?.quantity,
        selectedQuantity: selectedQuantity,
        concessionAmount: concessionAmount,
        products: modifiedProducts,
      });
      totalQuantity += alteration?.quantity;
    });
  });
  const modifiedDiscountedProductSelectorItems = DiscountedProductSelectorItems.sort(
    (a, b) => b.selectedQuantity - a.selectedQuantity,
  );
  return {
    items: modifiedDiscountedProductSelectorItems,
    totalItems: totalQuantity,
    totalSelectedItems: totalSelectedQuantity,
  };
};

export const getDiscountedProductSelectorPayload = (discounts: any): DiscountedProductSelectorPayloadType => {
  const selectedCoupons = discounts?.filter(
    (coupon: any) =>
      coupon?.coupon_metadata?.coupon_type === CouponType.BXGY && Boolean(coupon?.selected_items),
  );

  const selectedPayload: DiscountedProductSelectorPayloadType = {
    selection: selectedCoupons?.map((coupon: any) => {
      return {
        discount_code: coupon?.discount_code,
        items: coupon?.selected_items?.map((item: any) => {
          return {
            variant_id: item?.variant_id,
            quantity: item?.quantity,
          };
        }),
      };
    }),
  };

  return selectedPayload;
};
export const isImmutablePriceBand = (originalPrice: string, merchant: MerchantType | undefined) => {
  if (
    merchant?.immutablePriceBand?.maxPrice === null ||
    merchant?.immutablePriceBand?.maxPrice === undefined ||
    merchant?.immutablePriceBand?.minPrice === null ||
    merchant?.immutablePriceBand?.minPrice === undefined
  )
    return false;

  return (
    parseFloat(originalPrice) >= merchant?.immutablePriceBand?.minPrice &&
    parseFloat(originalPrice) <= merchant?.immutablePriceBand?.maxPrice
  );
};

export const getWishlistVisibilityConfig = (data: any): wishlistVisibilityConfigType => {
  return {
    enabled: data?.enabled ?? false,
    header: data?.header ?? "",
    emptyHeader: data?.empty_header ?? "",
  };
};

export const getWishlistConfig = (data: any): WishlistConfigType => {
  const defaultWishlistConfig: wishlistVisibilityConfigType = {
    enabled: false,
    header: "",
    emptyHeader: "",
  };
  if (!data)
    return {
      isEnabled: false,
      config: { checkout: defaultWishlistConfig, cart: defaultWishlistConfig, store: defaultWishlistConfig },
    };

  return {
    isEnabled: data?.enabled,
    config: {
      checkout: getWishlistVisibilityConfig(data?.config?.checkout),
      cart: getWishlistVisibilityConfig(data?.config?.cart),
      store: getWishlistVisibilityConfig(data?.config?.store),
    },
  };
};

export const setCheckoutUserType = async (checkoutId: string, userType: UserHistoryType) => {
  try {
    const response: any = await putRequest(
      `/checkout/${checkoutId}`,
      {
        note_attributes: [
          {
            name: "customer_type",
            value: userType,
          },
        ],
      },
      "KRATOS_PRIVATE",
    );
    if (Boolean(response?.error)) {
      throw new Error("unauthorized");
    }
  } catch (error) {
    console.error(error);
  }
};

export const getItemLevelAddOnsForItemIndex = (
  availableAddOns: Record<string, AddOnMetaType> | undefined,
  itemIndex: number,
  checkoutItems?: ItemType[],
): AddOnMetaType[] => {
  // An add-on whose target product is already granted as a freebie elsewhere in the cart
  // must never be offered for purchase, even if a stale `availableAddOns` state still lists
  // it (eg. a mutation response that didn't recompute item-level eligibility - see
  // CheckoutProvider's `trustItemLevelAddOns` gate).
  const freebieVariantIds = new Set(
    checkoutItems?.filter((item) => item?.is_freebie).map((item) => item.variant_id),
  );

  return Object.values(availableAddOns ?? {}).filter((addon) => {
    if (addon?.addon_level !== "ITEM") return false;
    if (freebieVariantIds.has(addon.variant_id)) return false;

    const referenceIds = addon.addon_reference_id;
    // Legacy payloads without mapping still show on every item.
    if (!Array.isArray(referenceIds)) return true;
    // Cart-level addons send an empty list.
    if (referenceIds.length === 0) return false;

    return referenceIds.includes(itemIndex);
  });
};

export const showAddOnLineItems = (
  availableAddOns?: Record<string, AddOnMetaType>,
  applliedAddOns?: AddOnType[],
): boolean => {
  if (!availableAddOns || !applliedAddOns) return false;
  const showLineItems = applliedAddOns?.some((item) => {
    const addOnData = availableAddOns[item?.addon_id];
    return addOnData?.add_line_item === true;
  });
  return showLineItems;
};

export const extractConflictingItemsData = (
  errorResponse: any,
  item_id: string,
  variant_id: string,
): CombinedProductRemovalItemsType => {
  const removableVariantIds =
    errorResponse?.error?.data?.map((item: any) => ({
      quantity: item?.quantity,
      variant_id: item?.variant_id,
    })) ?? [];
  return {
    selectedItem: {
      id: item_id ?? "",
      variantId: variant_id ?? "",
    },
    removableVariantIds,
  };
};

export const appendParamsToItemsUrl = (url: string, params: Record<string, string | null> = {}) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  // Get current page from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const page = urlParams.get("page");
  const isCartPage = ["cart", "empty-cart"].includes(page?.toLowerCase() ?? "");

  // Create URLSearchParams from existing URL
  const searchParams = new URLSearchParams(url.split("?")[1] || "");

  // Add new parameters
  Object.entries(params).forEach(([key, value]) => {
    if (key && value) {
      searchParams.set(key, value);
    }
  });

  // Add cart context if needed
  if (isCartPage) {
    searchParams.set("sf-context", "CART");
  }

  // Return final URL
  const queryString = searchParams.toString();
  const baseUrl = url.split("?")[0];
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const createCartToCheckoutURL = (cartUrl: string, checkoutId: string) => {
  let redirectUrl = new URL(cartUrl);
  let checkoutUrlSearchParams = new URLSearchParams(redirectUrl.search);
  checkoutUrlSearchParams.delete("page");
  checkoutUrlSearchParams.delete("referrer");
  checkoutUrlSearchParams.append("checkoutId", checkoutId);
  checkoutUrlSearchParams.append("referrer", "cart");
  redirectUrl.search = checkoutUrlSearchParams.toString();
  return redirectUrl;
};

export const isCartRedirectEnabled = () => {
  const searchParams = new URLSearchParams(document.location.search);
  return searchParams.get("is_cart_redirect") === "true";
};
